type Primitive = null | undefined | boolean | number | string | symbol | bigint;

function isPrimitive(value: unknown): value is Primitive {
  return (
    value === null || (typeof value !== 'object' && typeof value !== 'function')
  );
}

function deepEqualInternal(
  a: unknown,
  b: unknown,
  seen: WeakMap<object, object>,
): boolean {
  if (a === b) {
    return true;
  }
  if (isPrimitive(a) || isPrimitive(b)) {
    return a === b;
  }

  const aObj = a as object;
  const bObj = b as object;
  if (seen.get(aObj) === bObj) {
    return true;
  }
  seen.set(aObj, bObj);

  if (a instanceof Date && b instanceof Date) {
    return a.getTime() === b.getTime();
  }
  if (a instanceof RegExp && b instanceof RegExp) {
    return a.source === b.source && a.flags === b.flags;
  }
  if (a instanceof Map && b instanceof Map) {
    if (a.size !== b.size) {
      return false;
    }
    for (const [key, value] of a.entries()) {
      if (!b.has(key) || !deepEqualInternal(value, b.get(key), seen)) {
        return false;
      }
    }
    return true;
  }
  if (a instanceof Set && b instanceof Set) {
    if (a.size !== b.size) {
      return false;
    }
    const bValues = Array.from(b.values());
    for (const value of a.values()) {
      const matchIndex = bValues.findIndex((other) =>
        deepEqualInternal(value, other, seen),
      );
      if (matchIndex === -1) {
        return false;
      }
      bValues.splice(matchIndex, 1);
    }
    return bValues.length === 0;
  }
  if (Array.isArray(a) || Array.isArray(b)) {
    if (!Array.isArray(a) || !Array.isArray(b) || a.length !== b.length) {
      return false;
    }
    for (let i = 0; i < a.length; i++) {
      if (!deepEqualInternal(a[i], b[i], seen)) {
        return false;
      }
    }
    return true;
  }

  const aKeys = Object.keys(aObj);
  const bKeys = Object.keys(bObj);
  if (aKeys.length !== bKeys.length) {
    return false;
  }
  for (const key of aKeys) {
    if (!Object.prototype.hasOwnProperty.call(bObj, key)) {
      return false;
    }
    if (!deepEqualInternal((aObj as any)[key], (bObj as any)[key], seen)) {
      return false;
    }
  }
  return true;
}

export default function deepEqual(a: unknown, b: unknown): boolean {
  return deepEqualInternal(a, b, new WeakMap());
}
